import React from 'react';
import { useCommercialPage } from './hook';
import styled from 'styled-components';
import { SearchForm } from '../../../organisms/form/SearchForm';
import { PropertyCard } from '../../../molecules/PropertyCard';
import { Pagination } from '../../../organisms/Pagination';
import { CommercialData } from '../../../../core/api/request/property/commercial/types';
import { useLanguage } from '../../../../contexts/LanguageContext';

export const CommercialPage = () => {
  const { t } = useLanguage();
  const {
    properties,
    loading,
    searchProperties,
    formInitialValues,
    // Pagination
    currentPage,
    totalPages,
    handlePageChange,
    handlePrevious,
    handleNext,
    getCurrentSearchString,
  } = useCommercialPage();

  const handleSearch = (searchData: any) => {
    // Map the search form data to API parameters
    const searchParams = {
      status: searchData.propertyStatus,
      type: searchData.propertyType,
      district: searchData.district,
      commune: searchData.commune,
      popularPlace: searchData.popularPlace,
      bedrooms: searchData.bedrooms,
      priceMin: searchData.minPrice,
      priceMax: searchData.maxPrice,
      property_code: searchData.propertyCode,
    };

    searchProperties(searchParams);
  };

  return (
    <Container>
      <SectionTitle>{t('property.commercialProperty')}</SectionTitle>
      <SearchForm
        type="commercial"
        showTabs={false}
        onSubmit={handleSearch}
        categoryType="2"
        initialValues={formInitialValues}
      />

      <Section>
        {loading ? (
          <LoadingContainer>{t('common.loading')}</LoadingContainer>
        ) : (
          <>
            {properties.length === 0 ? (
              <EmptyContainer>{t('common.noPropertiesFound')}</EmptyContainer>
            ) : (
              <CardGrid>
                {properties.map((property: CommercialData) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    type="commercial"
                    returnUrl={getCurrentSearchString()}
                  />
                ))}
              </CardGrid>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                page={currentPage}
                totalPage={totalPages}
                onSetPage={handlePageChange}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
          </>
        )}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 12px;
  
  @media (min-width: 400px) {
    padding: 0 16px;
  }
  
  @media (min-width: 768px) {
    padding: 0 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 32px;
  }
`;

const Section = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 24px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    margin: 32px auto;
  }
  
  @media (min-width: 1024px) {
    margin: 48px auto;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 24px;
  color: #222;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 28px;
    margin-bottom: 28px;
  }
  
  @media (min-width: 1024px) {
    font-size: 32px;
    margin-bottom: 32px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  justify-items: center;
  margin-top: 20px;
  width: 100%;
  
  @media (min-width: 400px) {
    gap: 24px;
    margin-top: 24px;
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
    margin-top: 28px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
    margin-top: 32px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 16px;
  color: #666;
  text-align: center;
  width: 100%;
  
  @media (min-width: 768px) {
    font-size: 18px;
    min-height: 300px;
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 16px;
  color: #666;
  text-align: center;
  width: 100%;
  
  @media (min-width: 768px) {
    font-size: 18px;
    min-height: 300px;
  }
`;

